import React, { useEffect } from 'react';
import useSize from '@react-hook/size';
import {
  getCanvasFont,
  getWidth,
} from '../../components/Inputs/utils/elementText';

/**
 * Returns true if the element is truncated and overflowing, thus showing the ellipsis
 */
const isEllipsisActive = (parentWidth: number, elementWidth: number) => {
  return elementWidth > parentWidth;
};

export enum UsageType {
  /**
   * Default
   */
  General,
  /**
   * Determines which child element to target in order to calculate if ellipsis shown
   */
  CardHeader,
}

/**
 * Given a title, returns true if ellipsis is displayed and should show tooltip
 */
const useEllipsisDisplay = ({
  title,
  usage = UsageType.General,
}: {
  title: string;
  usage?: UsageType;
}) => {
  const target = React.useRef(null);
  const [width] = useSize(target);
  const [titleWidth, setTitleWidth] = React.useState<number | undefined>();
  const [tooltipDisplay, setTooltipDisplay] = React.useState(false);

  useEffect(() => {
    if (!target.current) return;

    // TODO - probably a better way to handle this for card header use case
    let child: HTMLElement | undefined | null = target.current;
    if (usage === UsageType.CardHeader) {
      const cardHeaderChildren = (target.current as HTMLElement)?.children;
      const firstChild = cardHeaderChildren?.[0] as HTMLElement;
      if (firstChild?.children) {
        child = firstChild?.children?.[0] as HTMLElement;
      }
    }

    let font = `700 14px Assistant`;
    if (child) font = getCanvasFont(child);

    let calculatedTitleWidth = titleWidth;
    if (!calculatedTitleWidth) {
      calculatedTitleWidth = getWidth(title, font);
    }
    if (!calculatedTitleWidth) return;
    setTitleWidth(calculatedTitleWidth);

    const isEllipsisShown = isEllipsisActive(width, calculatedTitleWidth);
    setTooltipDisplay(isEllipsisShown);
  }, [title, titleWidth, usage, width]);

  return {
    target,
    tooltipDisplay,
  };
};

export default useEllipsisDisplay;
